import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { AgentClientFrame, RealtimeEvent } from "@autopilot/shared";

type Listener = (event: RealtimeEvent) => void;

export interface RealtimeConnection {
  status: "connecting" | "open" | "closed";
  send(frame: AgentClientFrame): void;
  /** Returns an unsubscribe function. */
  subscribe(listener: Listener): () => void;
}

const RealtimeContext = createContext<RealtimeConnection | null>(null);

/**
 * One agent WebSocket per workspace (`/api/v1/w/:wid/ws`), reconnecting with backoff.
 * Consumers subscribe to `RealtimeEvent`s; React Query caches are updated by the subscribers.
 */
export function RealtimeProvider({ workspaceId, children }: { workspaceId: string; children: ReactNode }) {
  const [status, setStatus] = useState<RealtimeConnection["status"]>("connecting");
  const listeners = useRef(new Set<Listener>());
  const socket = useRef<WebSocket | null>(null);
  const pending = useRef<AgentClientFrame[]>([]);

  useEffect(() => {
    let closed = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let ping: ReturnType<typeof setInterval> | undefined;

    const connect = () => {
      const proto = location.protocol === "https:" ? "wss" : "ws";
      const ws = new WebSocket(`${proto}://${location.host}/api/v1/w/${workspaceId}/ws`);
      socket.current = ws;
      setStatus("connecting");
      ws.onopen = () => {
        attempt = 0;
        setStatus("open");
        for (const f of pending.current.splice(0)) ws.send(JSON.stringify(f));
        ping = setInterval(() => ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ type: "ping" })), 25_000);
      };
      ws.onmessage = (msg) => {
        if (typeof msg.data !== "string" || msg.data === "pong") return;
        const event = JSON.parse(msg.data) as RealtimeEvent;
        for (const l of listeners.current) l(event);
      };
      ws.onclose = () => {
        clearInterval(ping);
        setStatus("closed");
        if (closed) return;
        attempt++;
        timer = setTimeout(connect, Math.min(15_000, 500 * 2 ** attempt));
      };
    };
    connect();
    return () => {
      closed = true;
      clearTimeout(timer);
      clearInterval(ping);
      socket.current?.close();
    };
  }, [workspaceId]);

  const value = useMemo<RealtimeConnection>(
    () => ({
      status,
      send(frame) {
        const ws = socket.current;
        if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(frame));
        else if (frame.type !== "ping") pending.current.push(frame);
      },
      subscribe(listener) {
        listeners.current.add(listener);
        return () => listeners.current.delete(listener);
      },
    }),
    [status],
  );
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeConnection {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error("useRealtime must be used inside RealtimeProvider");
  return ctx;
}

/** Subscribes for the component lifetime. `handler` may change between renders. */
export function useRealtimeEvent(handler: Listener): void {
  const rt = useRealtime();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => rt.subscribe((e) => ref.current(e)), [rt.subscribe]);
}
